import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const leadSchema = z.object({
  firstName: z.string().trim().min(1).max(80),
  email: z.string().trim().email().max(255),
  zip: z.string().trim().max(20).optional().nullable(),
  isInternational: z.boolean(),
  interests: z.array(z.string().max(60)).max(20).default([]),
  userAgent: z.string().max(500).optional().nullable(),
  referrer: z.string().max(500).optional().nullable(),
});

const eventSchema = z.object({
  eventName: z.enum([
    "offer_form_submit",
    "offer_book_click",
    "offer_copy_code",
    "offer_email_request",
    "offer_dialog_auto_open",
  ]),
  offerCode: z.string().max(40).optional().nullable(),
  email: z.string().email().max(255).optional().nullable(),
  metadata: z
    .record(z.string(), z.unknown())
    .optional()
    .superRefine((val, ctx) => {
      if (val && JSON.stringify(val).length > 4096) {
        ctx.addIssue({
          code: "custom",
          message: "metadata exceeds 4096 bytes",
        });
      }
    }),
});

export const submitOfferLead = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => leadSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { error } = await supabaseAdmin.from("offer_leads").insert({
      first_name: data.firstName,
      email: data.email,
      zip: data.isInternational ? null : (data.zip ?? null),
      is_international: data.isInternational,
      interests: data.interests,
      user_agent: data.userAgent ?? null,
      referrer: data.referrer ?? null,
    });
    if (error) throw new Error(error.message);

    await supabaseAdmin.from("offer_events").insert({
      event_name: "offer_form_submit",
      email: data.email,
      metadata: { interests: data.interests, isInternational: data.isInternational } as never,
    });

    return { ok: true as const };
  });

export const logOfferEvent = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => eventSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { error } = await supabaseAdmin.from("offer_events").insert({
      event_name: data.eventName,
      offer_code: data.offerCode ?? null,
      email: data.email ?? null,
      metadata: (data.metadata ?? {}) as never,
    });
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });
